import { claimFeatureCode } from '@/apiServices';
import { MaxLeverageProvider } from '@/context/MaxLeverageContext';
import { AccountBalanceProvider } from '@/pages/dashboard/orderEntry/AccountBalanceContext';
import { ExchangeTickerProvider } from '@/shared/context/ExchangeTickerProvider';
import { useFeatureFlag } from '@/shared/context/FeatureFlagProvider';
import { InitialLoadDataProvider } from '@/shared/context/InitialLoadDataProvider';
import { useToast } from '@/shared/context/ToastProvider';
import pixelLogo from '@images/graphics/pixel_logo.png';
import { Box, Button, CircularProgress, Container, Divider, Stack, useTheme, TextField } from '@mui/material';
import React, { useMemo, useState } from 'react';
import MarketMakerEntryForm from './components/MarketMakerEntryForm';
import MarketMakerHistory from './components/MarketMakerHistory';
import { useMarketMakerHistory } from './hooks/useMarketMakerHistory';
import PrismaticBurst from './PrismaticBurst';

export default function MarketMakerPage() {
  const theme = useTheme();
  const { isFeatureEnabled } = useFeatureFlag();
  const isEnabled = isFeatureEnabled('market_maker');
  const { showToastMessage } = useToast();
  const [code, setCode] = useState('');
  const [isClaimingFeature, setIsClaimingFeature] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(false);

  const statusFilter = useMemo(() => (showActiveOnly ? ['ACTIVE', 'PAUSED', 'FINISHER'] : []), [showActiveOnly]);
  const history = useMarketMakerHistory(statusFilter);

  const claimFeature = async (claimCode) => {
    setIsClaimingFeature(true);
    try {
      const response = await claimFeatureCode(claimCode);
      showToastMessage({
        type: 'success',
        message: response.message,
      });

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      showToastMessage({
        type: 'error',
        message: error.message,
      });
      setIsClaimingFeature(false);
    }
  };

  if (!isEnabled) {
    return (
      <>
        <PrismaticBurst
          animationType='rotate3d'
          colors={[theme.palette.primary.main, theme.palette.secondary.main, theme.palette.text.primary]}
          distort={1.0}
          hoverDampness={0.25}
          intensity={2}
          mixBlendMode='lighten'
          offset={{ x: 0, y: 0 }}
          rayCount={0}
          speed={0.5}
        />
        <Container
          height='100%'
          maxWidth='xl'
          sx={{
            height: '100%',
            py: 4,
            backgroundColor: 'transparent',
            backdropFilter: 'none',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <img
              alt='Volume Bot Logo'
              src={pixelLogo}
              style={{
                width: '500px',
                height: '500px',
                objectFit: 'contain',
              }}
            />
          </Box>
          <Box sx={{ width: '500px', mx: 'auto' }}>
            <Stack direction='row' spacing={2}>
              <TextField
                fullWidth
                label='Enter Alpha Code'
                value={code}
                variant='outlined'
                onChange={(e) => setCode(e.target.value)}
              />
              <Button
                disabled={isClaimingFeature || !code.trim()}
                sx={{ minWidth: '100px' }}
                variant='contained'
                onClick={() => claimFeature(code)}
              >
                {isClaimingFeature ? <CircularProgress size={20} /> : 'Access'}
              </Button>
            </Stack>
          </Box>
        </Container>
      </>
    );
  }

  return (
    <InitialLoadDataProvider>
      <ExchangeTickerProvider exchangeName='Hyperliquid'>
        <MaxLeverageProvider>
          <AccountBalanceProvider>
            <PrismaticBurst
              animationType='rotate3d'
              colors={[theme.palette.primary.main, theme.palette.secondary.main, theme.palette.text.primary]}
              distort={1.0}
              hoverDampness={0.25}
              intensity={2}
              mixBlendMode='lighten'
              offset={{ x: 0, y: 0 }}
              rayCount={0}
              speed={0.5}
            />
            {/* </Box> */}
            <Container
              maxWidth={false}
              sx={{
                maxWidth: '1230px',
                p: 4,
                my: 4,
                position: 'relative',
                zIndex: 1,
                backdropFilter: 'blur(10px)',
                backgroundColor: 'rgba(10,12,24,0.45)',
                borderRadius: 4,
                boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
              }}
            >
              <Stack direction='column' spacing={3}>
                <MarketMakerEntryForm
                  activeOrders={history?.marketMakerOrders || []}
                  lifetime={history?.lifetimeStats}
                />
                <Divider />
                <MarketMakerHistory
                  history={history}
                  setShowActiveOnly={setShowActiveOnly}
                  showActiveOnly={showActiveOnly}
                />
              </Stack>
            </Container>
          </AccountBalanceProvider>
        </MaxLeverageProvider>
      </ExchangeTickerProvider>
    </InitialLoadDataProvider>
  );
}
